import { faTelescope } from '@fortawesome/pro-duotone-svg-icons';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TemplateId } from 'api/models';
import { Button } from 'components/atoms/button';
import {
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { Loading } from 'components/atoms/loading';
import { FormSection } from 'components/molecules/forms/form-fields/styles';
import { FormQuestionRenderer } from 'components/organisms/forms';
import { BodyShort01 } from 'components/styles';
import { RoutePaths } from 'constant';
import { useFormTemplatesQuery } from 'queries/form-template';

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 320px;
`;

const FormWrapper = styled.div`
  padding: 0 var(--layout-03) var(--spacing-09) var(--layout-03);
`;
const FormDesc = styled.div`
  ${BodyShort01};
  color: var(--gray-80);
  padding: var(--spacing-07) 0 0;
`;
const FormElements = styled.div`
  margin-top: var(--spacing-07);
  section {
    ${FormSection};
    margin-bottom: var(--spacing-05);
  }
`;

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    text-align: left;
    padding: var(--spacing-11) 0px;
  }
`;
const LinkButton = styled(Button)`
  padding: var(--spacing-02) 0;
`;

export const ProjectPlanFormDetails: FC<{
  formTemplateId: number;
}> = ({ formTemplateId }) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useForm();
  const { data: formTemplate, isLoading: isFormTemplateLoading } =
    useFormTemplatesQuery(TemplateId(formTemplateId));

  const openFormTemplate = () => {
    const formTemplateUrl = RoutePaths.formTemplate.replace(
      ':templateId',
      formTemplateId.toString(),
    );

    window.open(formTemplateUrl, '_blank');
  };

  return isFormTemplateLoading || !formTemplate ? (
    <LoadingWrapper>
      <Loading />
    </LoadingWrapper>
  ) : (
    <FormWrapper>
      {formTemplate.description && (
        <FormDesc>{formTemplate.description}</FormDesc>
      )}
      <FormElements>
        {formTemplate.formQuestions.length ? (
          formTemplate.formQuestions.map((question) => (
            <FormQuestionRenderer
              question={question}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled
            />
          ))
        ) : (
          <EmptyStateContainer>
            <EmptyState
              image={<EmptyStateIcon icon={faTelescope} />}
              title={<FormattedMessage id="forms.response.no_question.title" />}
              message={
                <>
                  <FormattedMessage id="forms.response.no_question.message" />
                  <div>
                    <FormattedMessage id="forms.response.configure_form_message" />
                    <LinkButton type="link" onClick={openFormTemplate}>
                      <FormattedMessage id="templates.form_template" />
                    </LinkButton>
                  </div>
                </>
              }
              secondaryCTA={null}
            />
          </EmptyStateContainer>
        )}
      </FormElements>
    </FormWrapper>
  );
};
